<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">{{ editing ? 'Editar mantenimiento' : 'Registrar mantenimiento' }}</h1>
    </div>

    <form class="custom-form" @submit.prevent="save" novalidate>
      <b-card tag="article" no-body style="max-width: 920px" class="custom-card">
        <b-card-header header-tag="section" class="custom-card-header d-flex align-items-center">
          <h3>Datos del mantenimiento</h3>
        </b-card-header>
        <b-card-body class="custom-card-body p-0">
          <section class="form-section">
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label>Vehículo *</label>
                  <select v-model="form.vehicle_id" class="custom-form-control"
                    :class="{ 'custom-form-control-error': $v.form.vehicle_id.$error }">
                    <option :value="null" disabled>Seleccionar vehículo</option>
                    <option v-for="v in vehicles" :key="v.id" :value="v.id">{{ v.license_plate }}</option>
                  </select>
                  <FormError v-if="$v.form.vehicle_id.$error && !$v.form.vehicle_id.required"
                    message="El vehículo es requerido" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label>Fecha *</label>
                  <input type="date" v-model="form.maintenance_date" class="custom-form-control"
                    :class="{ 'custom-form-control-error': $v.form.maintenance_date.$error }" />
                  <FormError v-if="$v.form.maintenance_date.$error && !$v.form.maintenance_date.required"
                    message="La fecha es requerida" />
                </div>
              </div>

              <div class="col-md-6">
                <div class="form-group">
                  <label>Tipo *</label>
                  <select v-model="form.maintenance_type" class="custom-form-control"
                    :class="{ 'custom-form-control-error': $v.form.maintenance_type.$error }">
                    <option :value="null" disabled>Seleccionar tipo</option>
                    <option v-for="t in maintTypes" :key="t.key" :value="t.key">{{ t.label }}</option>
                  </select>
                  <FormError v-if="$v.form.maintenance_type.$error && !$v.form.maintenance_type.required"
                    message="El tipo es requerido" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label>Odómetro (km) *</label>
                  <input type="number" min="0" step="1" v-model.number="form.odometer_km"
                    class="custom-form-control" placeholder="0"
                    :class="{ 'custom-form-control-error': $v.form.odometer_km.$error }" />
                  <FormError v-if="$v.form.odometer_km.$error && !$v.form.odometer_km.required"
                    message="El odómetro es requerido" />
                  <FormError v-if="$v.form.odometer_km.$error && !$v.form.odometer_km.minValue"
                    message="El odómetro no puede ser negativo" />
                </div>
              </div>

              <div class="col-md-6">
                <div class="form-group">
                  <label>Taller (opcional)</label>
                  <input type="text" v-model="form.workshop_name" class="custom-form-control"
                    placeholder="Nombre del taller" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label>Dirección del taller (opcional)</label>
                  <input type="text" v-model="form.workshop_address" class="custom-form-control"
                    placeholder="Dirección" />
                </div>
              </div>

              <div class="col-md-4">
                <div class="form-group">
                  <label>Costo (opcional)</label>
                  <input type="number" step="0.01" min="0" v-model.number="form.cost"
                    class="custom-form-control" placeholder="0.00" />
                </div>
              </div>
              <div class="col-md-2">
                <div class="form-group">
                  <label>Moneda</label>
                  <select v-model="form.currency" class="custom-form-control">
                    <option value="PEN">PEN</option>
                    <option value="USD">USD</option>
                  </select>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label>Próximo mantenimiento (km, opcional)</label>
                  <input type="number" min="0" step="1" v-model.number="form.next_maintenance_km"
                    class="custom-form-control" placeholder="Si se deja vacío, se calcula con el intervalo" />
                </div>
              </div>

              <div class="col-md-12">
                <div class="form-group">
                  <label>Descripción</label>
                  <textarea v-model="form.description" class="custom-form-control" rows="2"
                    placeholder="Trabajo realizado"></textarea>
                </div>
              </div>
              <div class="col-md-12">
                <div class="form-group mb-0">
                  <label>Notas</label>
                  <textarea v-model="form.notes" class="custom-form-control" rows="2"
                    placeholder="Observaciones"></textarea>
                </div>
              </div>
            </div>
          </section>

          <section class="form-section">
            <h2 class="form-subtitle">Factura</h2>
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label>Foto de la factura</label>
                  <input type="file" accept="image/*" class="custom-form-control" @change="onFile($event)" />
                  <small v-if="existing.has_photo" class="d-block mt-1">
                    <button type="button" class="photo-link" @click="viewPhoto">Ver factura actual</button>
                  </small>
                </div>
              </div>
            </div>
            <p class="text-muted small mb-0">Imagen hasta 5 MB.</p>
          </section>

          <section class="form-section">
            <div class="form-group-buttons text-right">
              <router-link :to="{ name: 'fleet-maintenance-list' }" class="button button-light">
                {{ editing ? 'Volver' : 'Cancelar' }}
              </router-link>
              <button class="button button-primary" type="submit">Guardar</button>
            </div>
          </section>
        </b-card-body>
      </b-card>
    </form>

    <b-modal v-model="photoModal.show" size="lg" hide-footer title="Factura del mantenimiento">
      <div class="photo-frame text-center">
        <img v-if="photoModal.url" :src="photoModal.url" alt="Factura" class="photo-img" />
        <p v-else-if="photoModal.error" class="text-muted m-0 py-4">{{ photoModal.error }}</p>
        <p v-else class="text-muted m-0 py-4">Cargando imagen…</p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { Alert, FormError } from 'wize-admin'
import { required, minValue } from 'vuelidate/lib/validators'
import { FleetMaintenanceService, VehicleService } from '@/core/services'

const MAINT_TYPES = [
  { key: 'SCHEDULED', label: 'Programado' },
  { key: 'CORRECTIVE', label: 'Correctivo' }
]
const MAX_PHOTO_BYTES = 5 * 1024 * 1024

export default {
  name: 'MaintenanceEditor',
  components: { FormError },
  data() {
    return {
      editing: false,
      vehicles: [],
      maintTypes: MAINT_TYPES,
      form: {
        vehicle_id: null,
        maintenance_date: null,
        maintenance_type: null,
        odometer_km: null,
        workshop_name: null,
        workshop_address: null,
        cost: null,
        currency: 'PEN',
        next_maintenance_km: null,
        description: null,
        notes: null
      },
      photoFile: null,
      existing: { has_photo: false },
      photoModal: { show: false, url: null, error: null }
    }
  },
  validations: {
    form: {
      vehicle_id: { required },
      maintenance_date: { required },
      maintenance_type: { required },
      odometer_km: { required, minValue: minValue(0) }
    }
  },
  methods: {
    onFile(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) { this.photoFile = null; return }
      if (!file.type.startsWith('image/')) {
        Alert.error('El archivo debe ser una imagen')
        event.target.value = ''
        return
      }
      if (file.size > MAX_PHOTO_BYTES) {
        Alert.error('La imagen supera el máximo de 5 MB')
        event.target.value = ''
        return
      }
      this.photoFile = file
    },
    buildPayload() {
      const f = this.form
      return {
        vehicle_id: f.vehicle_id,
        maintenance_date: f.maintenance_date,
        maintenance_type: f.maintenance_type,
        odometer_km: Number(f.odometer_km),
        workshop_name: f.workshop_name || null,
        workshop_address: f.workshop_address || null,
        cost: f.cost != null && f.cost !== '' ? Number(f.cost) : null,
        currency: f.currency || 'PEN',
        next_maintenance_km: f.next_maintenance_km != null && f.next_maintenance_km !== '' ? Number(f.next_maintenance_km) : null,
        description: f.description || null,
        notes: f.notes || null
      }
    },
    async save() {
      this.$v.$touch()
      if (this.$v.$error) {
        this.$nextTick(() => {
          this.$smoothScroll({ scrollTo: document.querySelector('.form-error'), updateHistory: false, offset: -50 })
        })
        return
      }
      try {
        await this.$store.dispatch('app/loading', true)
        const payload = this.buildPayload()

        if (!this.editing) {
          const res = await FleetMaintenanceService.create(payload)
          const id = res.payload.id
          if (this.photoFile) await FleetMaintenanceService.uploadPhoto(id, this.photoFile)
          Alert.success('Mantenimiento registrado correctamente')
          this.$router.push({ name: 'fleet-maintenance-list' })
        } else {
          const id = this.$route.params.id
          await FleetMaintenanceService.update(id, payload)
          if (this.photoFile) await FleetMaintenanceService.uploadPhoto(id, this.photoFile)
          this.photoFile = null
          Alert.success('Mantenimiento actualizado correctamente')
          await this.load(id)
        }
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'No se pudo guardar el mantenimiento')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    async load(id) {
      try {
        await this.$store.dispatch('app/loading', true)
        const res = await FleetMaintenanceService.get(id)
        const p = res.payload
        this.form = {
          vehicle_id: p.vehicle_id,
          maintenance_date: p.maintenance_date ? p.maintenance_date.slice(0, 10) : null,
          maintenance_type: p.maintenance_type,
          odometer_km: p.odometer_km,
          workshop_name: p.workshop_name,
          workshop_address: p.workshop_address,
          cost: p.cost,
          currency: p.currency || 'PEN',
          next_maintenance_km: p.next_maintenance_km,
          description: p.description,
          notes: p.notes
        }
        this.existing = { has_photo: p.has_photo }
        this.editing = true
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'No se pudo cargar el mantenimiento')
        this.$router.push({ name: 'fleet-maintenance-list' })
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    async viewPhoto() {
      this.photoModal = { show: true, url: null, error: null }
      try {
        const res = await FleetMaintenanceService.getPhoto(this.$route.params.id)
        this.photoModal.url = res.payload ? res.payload.url : null
        if (!this.photoModal.url) this.photoModal.error = 'No hay factura disponible.'
      } catch (e) {
        console.error(e)
        this.photoModal.error = e.errors ? e.errors.message : 'No se pudo cargar la factura.'
      }
    },
    async loadVehicles() {
      try {
        const res = await VehicleService.list({ is_travelcab: true }, { page: 0, size: 1000 })
        const payload = res.payload || {}
        this.vehicles = payload.items || payload || []
      } catch (e) { console.error(e) }
    }
  },
  async created() {
    await this.loadVehicles()
    if (this.$route.params.id) {
      await this.load(this.$route.params.id)
    } else if (this.$route.query.vehicle_id) {
      // Precarga el vehículo cuando se registra desde el dashboard.
      this.form.vehicle_id = Number(this.$route.query.vehicle_id)
    }
  }
}
</script>

<style lang="stylus" scoped>
.photo-link
  background none
  border none
  padding 0
  color #007bff
  cursor pointer
  text-decoration underline
.photo-frame
  background #f4f5f7
  border 1px solid #e5e7eb
  border-radius 8px
  min-height 220px
  display flex
  align-items center
  justify-content center
  overflow hidden
.photo-img
  max-width 100%
  max-height 70vh
  object-fit contain
</style>
